import type { TextRenderInput, TextRenderListMarkerStyle } from '@/framework/services/text-render';
import { colorToRgbaUint32 } from '@/utils/color-kit';
import { TextWritingType, type ILayerProps, type IVectorLayerProps } from '@/version-types';

const CHARACTER_LIST_MARKER_STYLES = new Set<TextRenderListMarkerStyle>([
    'decimal',
    'lower-latin',
    'upper-roman',
    'disc',
    'circle',
    'square',
]);

/**
 * 将 Vector Text 直接转换为宿主无关的普通文字渲染输入。
 *
 * Vector 只声明整段纯文字和字符型 marker；富文本分段、高级素材和文字特效均保持关闭。
 * padding 由 Vector 宿主负责，因此 renderer 只接收扣除 padding 后的固定行内尺寸。
 *
 * @param layer - 带有 textInfo 的真实 Vector Layer 或同 ID 工作值。
 * @returns Vector 宿主 Renderer 使用的完整输入。
 */
export const createTextRenderInputFromVectorLayer = (layer: ILayerProps): TextRenderInput => {
    const textInfo = (layer.data as IVectorLayerProps).textInfo!;
    const padding = textInfo.padding ?? { top: 0, right: 0, bottom: 0, left: 0 };
    const isHorizontal = textInfo.writingMode === TextWritingType.HORIZONTAL_TB;
    const inlineSize = isHorizontal
        ? Math.max(layer.width - padding.left - padding.right, 1)
        : Math.max(layer.height - padding.top - padding.bottom, 1);
    const listMarkerStyle = textInfo.listStyle as TextRenderListMarkerStyle;
    const listMarker: TextRenderInput['listMarker'] = CHARACTER_LIST_MARKER_STYLES.has(listMarkerStyle)
        ? { kind: 'character', style: listMarkerStyle }
        : undefined;

    const input: TextRenderInput = {
        text: textInfo.text,
        fontFamily: textInfo.fontFamily,
        fontSize: textInfo.fontSize,
        colorRgba: colorToRgbaUint32(textInfo.color),
        bold: textInfo.bold,
        italic: textInfo.italic,
        lineThrough: textInfo.lineThrough,
        underline: textInfo.underline,

        spans: [],

        lineHeightScale: textInfo.lineSpacing,
        letterSpacing: textInfo.letterSpacing,
        align: textInfo.textAlign,
        writingMode: textInfo.writingMode as TextRenderInput['writingMode'],
        width: isHorizontal ? inlineSize : null,
        height: isHorizontal ? null : inlineSize,
    };

    if (listMarker) input.listMarker = listMarker;

    return input;
};
